import axios, { AxiosResponse } from 'axios';

// 从URL获取link参数
export function getLinkFromUrl(): string | null {
  const pathParts = window.location.pathname.split('/');
  // URL formats: /gallery/LINK_TOKEN or /display/LINK_TOKEN
  if ((pathParts[1] === 'gallery' || pathParts[1] === 'display') && pathParts[2]) {
    return pathParts[2];
  }
  return null;
}

// 动态获取API地址
const getApiBaseUrl = () => {
  // 优先使用环境变量
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // 生产环境使用当前域名
  if (process.env.NODE_ENV === 'production') {
    return `${window.location.protocol}//${window.location.host}/api`;
  }
  // 开发环境默认
  return 'http://localhost:8000/api';
};

// 创建axios实例
const request = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  (error) => {
    const errorMessage = error.response?.data?.message || error.message || '请求失败';
    return Promise.reject(new Error(errorMessage));
  }
);

// API接口定义
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

export interface VerifyLinkResponse {
  success: boolean;
  message?: string;
  user?: string;
  face_enabled?: boolean;
}

export interface ImageFile {
  id?: number;
  name: string;
  path: string;
  size: number;
  size_formatted?: string;
  mtime: string;
  last_modified: string;
  isdir: boolean;
  url: string;
  thumbnail_url: string;
  key: string;
  etag: string;
  score?: number;
  faceBbox?: string; // "left,top,right,bottom" of the matched face
}

export interface Folder {
  name: string;
  path: string;
}

export interface PaginationInfo {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

export interface ImagesResponse {
  success: boolean;
  images: ImageFile[];
  pagination: PaginationInfo;
  message?: string;
}

export interface FoldersResponse {
  success: boolean;
  folders: Folder[];
  count: number;
  message?: string;
}

export interface HeaderImageResponse {
  success: boolean;
  header_url: string | null;
  header_url_xs?: string | null;
  header_url_md?: string | null;
  has_custom: boolean;
  message?: string;
}

export interface BackgroundImageResponse {
  success: boolean;
  background_url: string | null;
  has_custom: boolean;
  message?: string;
}

export interface LogoImageResponse {
  success: boolean;
  logo_url: string | null;
  logo_size: number | null;
  has_custom: boolean;
  message?: string;
}

export interface StyleResponse {
  success: boolean;
  header_height_xs: number;
  header_height_md: number;
  header_width_xs?: number;
  header_width_md?: number;
  logo_height: number;
  header_overlay_opacity: number;
  background_overlay_opacity: number;
  message?: string;
}

export interface DeleteImageResponse {
  success: boolean;
  deleted: string;
  message?: string;
}

// 获取完整的API URL（用于直接访问资源）
const getFullApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  if (process.env.NODE_ENV === 'production') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'http://localhost:8000';
};

// API方法
export const api = {
  // 验证link是否有效
  verifyLink: (link: string): Promise<VerifyLinkResponse> => {
    return request.get(`verify-link/${link}`);
  },

  // 获取header背景图片
  getHeaderImage: (link: string): Promise<HeaderImageResponse> => {
    return request.get(`gallery/${link}/header-image`);
  },

  // 获取logo图片
  getLogoImage: (link: string): Promise<LogoImageResponse> => {
    return request.get(`gallery/${link}/logo-image`);
  },

  // 获取页面背景图片
  getBackgroundImage: (link: string): Promise<BackgroundImageResponse> => {
    return request.get(`gallery/${link}/background-image`);
  },

  // 获取展示样式配置（默认值 + 用户自定义后的生效值）
  getStyle: (link: string): Promise<StyleResponse> => {
    return request.get(`gallery/${link}/style`);
  },

  // 获取文件夹列表
  getFolders: (link: string): Promise<FoldersResponse> => {
    return request.get(`gallery/${link}/folders`);
  },

  // 获取图片列表
  getImages: (link: string, folderPath: string = '', page: number = 1, perPage: number = 20, sortBy: string = 'time', sortOrder: string = 'desc'): Promise<ImagesResponse> => {
    return request.get(`gallery/${link}/images`, {
      params: {
        path: folderPath,
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder
      }
    });
  },

  // 获取缩略图URL（直接返回URL，不需要调用）
  getThumbnailUrl: (link: string, imagePath: string, size: 'small' | 'large' = 'small'): string => {
    const baseUrl = getFullApiUrl();
    return `${baseUrl}/api/gallery/${link}/thumbnail?path=${encodeURIComponent(imagePath)}&size=${size}`;
  },

  // 获取下载URL
  getDownloadUrl: (link: string, imagePath: string): string => {
    const baseUrl = getFullApiUrl();
    return `${baseUrl}/api/gallery/${link}/download?path=${encodeURIComponent(imagePath)}`;
  },

  // 获取二维码URL
  getQRCodeUrl: (link: string, imagePath: string, size: number = 200): string => {
    const downloadUrl = api.getDownloadUrl(link, imagePath);
    const baseUrl = getFullApiUrl();
    return `${baseUrl}/api/gallery/${link}/qrcode?data=${encodeURIComponent(downloadUrl)}&size=${size}`;
  },

  // 删除图片
  deleteImage: (link: string, imagePath: string): Promise<DeleteImageResponse> => {
    return request.post(`gallery/${link}/delete`, {
      path: imagePath
    });
  },

  // 人脸搜索（用 fetch 繞過 axios 默認 Content-Type，讓瀏覽器自動設置 multipart boundary）
  // 支持最大 10MB 圖片上傳，超時時間 120 秒
  faceSearch: (link: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const baseUrl = getApiBaseUrl();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout
    return fetch(`${baseUrl}/gallery/${link}/face-search`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    }).then(res => {
      clearTimeout(timeoutId);
      return res.json();
    }).catch(err => {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error('上傳超時，請使用較小的圖片或檢查網絡連接');
      }
      throw err;
    });
  },

  // 批量下載（返回 ZIP 文件）
  batchDownload: (link: string, paths: string[]): Promise<Blob> => {
    const baseUrl = getApiBaseUrl();
    return fetch(`${baseUrl}/gallery/${link}/batch-download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paths),
    }).then(res => {
      if (!res.ok) throw new Error('Batch download failed');
      return res.blob();
    });
  },

  // 人脸搜索统计（搜索人数、累计找到的照片数）
  faceSearchStats: (link: string): Promise<{ success: boolean; peopleCount?: number; photoCount?: number; message?: string }> => {
    return request.get(`gallery/${link}/face-search-stats`);
  },

  // 按图片 ID 列表（含人脸框坐标）恢复搜索结果（扫码打开分享链接时使用）
  faceResults: (link: string, data: string): Promise<{ success: boolean; count?: number; images?: ImageFile[]; message?: string }> => {
    return request.get(`gallery/${link}/face-results`, {
      params: { data }
    });
  }
};

export default request;
